import {useEffect, useMemo, useRef, useState} from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {cn} from '@common-web/lib/utils';
import {
  DEFAULT_MIRROR_FIELDS,
  MIRROR_FIELDS,
  type MirrorFieldKey,
} from '@common/enrollmentSite/mirror';
import type {GroupConfig, PayPeriod} from '@common/enrollmentSite/types';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';

interface SectionOption {
  id: string;
  label: string;
  icon: LucideIcon;
  hint?: string;
}

// Main products pre-checked when the source offers them (and unchecked when it doesn't).
const SOURCE_DEFAULT_PRODUCTS = ['medical', 'limited-medical', 'dental', 'vision'] as const;
// A site needs at least one of these to publish.
const CORE_PRODUCTS = ['medical', 'limited-medical'] as const;

// Builder sections that are always the site's own.
const ALWAYS_OWN_SECTIONS = new Set(['brand', 'e123']);

type Source = {groupSlug: string; name: string};

export interface MirrorDialogProps {
  mode: 'start' | 'settings';
  groupSlug: string;
  /** This site's current draft, to spot products whose own plans mirroring would replace. */
  siteConfig: GroupConfig | null;
  /** Sets this site's pay period (and e123 billing period) as an unsaved edit. */
  onSetPayPeriod: (period: PayPeriod) => void;
  sections: readonly SectionOption[];
  /** Settings mode: the current source and fields. */
  source?: Source;
  initialFields?: readonly string[];
  dirty: boolean;
  /** Saves unsaved edits and returns the new saved timestamp. */
  saveFirst: () => Promise<string | undefined>;
  lastKnownUpdatedAt: string | undefined;
  onApplied: (res: {siteConfig: GroupConfig; updatedAt: string | Date}, source: Source) => void;
  onClose: () => void;
}

export function MirrorDialog({
  mode,
  groupSlug,
  siteConfig,
  onSetPayPeriod,
  sections,
  source: currentSource,
  initialFields,
  dirty,
  saveFirst,
  lastKnownUpdatedAt,
  onApplied,
  onClose,
}: MirrorDialogProps) {
  const [source, setSource] = useState<Source | null>(currentSource ?? null);
  const {data: sites, isLoading} = trpc.internal.enrollmentSite.list.useQuery();
  const [fields, setFields] = useState<Set<string>>(
    () => new Set(initialFields?.length ? initialFields : DEFAULT_MIRROR_FIELDS),
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Sources can't themselves be mirrors (no chains).
  const candidates = (sites ?? []).filter((s) => s.groupSlug !== groupSlug && !s.sourceSite);
  const sourceCategories = new Set(
    sites?.find((s) => s.groupSlug === source?.groupSlug)?.productCategories ?? [],
  );
  const {data: sourceSite} = trpc.internal.enrollmentSite.get.useQuery(
    {groupSlug: source?.groupSlug ?? ''},
    {enabled: !!source},
  );
  // Recommended fields, with the main products checked only when the source offers them.
  function defaultFieldsFor(sourceSlug: string): Set<string> {
    const offered = new Set(sites?.find((s) => s.groupSlug === sourceSlug)?.productCategories ?? []);
    const next = new Set<string>(DEFAULT_MIRROR_FIELDS);
    for (const category of SOURCE_DEFAULT_PRODUCTS) {
      if (offered.has(category)) next.add(`product:${category}`);
      else next.delete(`product:${category}`);
    }
    return next;
  }

  function pickSource(picked: Source) {
    setSource(picked);
    if (mode === 'start') setFields(defaultFieldsFor(picked.groupSlug));
  }

  // Whether the site would still have medical or limited medical once these fields apply.
  const keepsCoreProduct = CORE_PRODUCTS.some((category) =>
    fields.has(`product:${category}`)
      ? sourceCategories.has(category)
      : !!siteConfig?.products.some((p) => p.category === category),
  );

  // Rates are entered per pay period, which each site keeps as its own.
  const sourcePeriod = sourceSite ? sitePayPeriod(sourceSite.siteConfig) : undefined;
  const ownPeriod = siteConfig ? sitePayPeriod(siteConfig) : undefined;
  const ratesPeriodMismatch =
    fields.has('rates') && !!sourcePeriod && !!ownPeriod && sourcePeriod !== ownPeriod;

  // Plans this site has in `category` that the source's plans wouldn't keep (plan ids differ
  // unless the site was copied from the source).
  const replacedPlanCount = (category: string) => {
    const own = siteConfig?.products.find((p) => p.category === category)?.planIds ?? [];
    const kept = new Set(
      sourceSite?.siteConfig.products.find((p) => p.category === category)?.planIds ?? [],
    );
    return own.filter((id) => !kept.has(id)).length;
  };
  // A checked category the source lacks still follows it: if the source adds it later, it arrives here.
  const fieldNote = (key: string) => {
    const f = MIRROR_FIELDS.find((m) => m.key === key);
    if (!f || !('product' in f)) return undefined;
    const notes: string[] = [];
    if (sites && !sourceCategories.has(f.product)) notes.push(`not on ${source?.name}`);
    const newlyMirrored = fields.has(key) && !initialFields?.includes(key);
    const replaced = newlyMirrored && sourceSite ? replacedPlanCount(f.product) : 0;
    if (replaced > 0) {
      notes.push(
        `replaces ${replaced} of this site’s plans${fields.has('rates') ? '' : ', new plans start at $0'}; remap their PDIDs`,
      );
    }
    return notes.join('; ') || undefined;
  };

  const start = trpc.internal.enrollmentSite.startMirroring.useMutation({
    onError: createMutationErrorHandler('Could not start mirroring'),
  });
  const update = trpc.internal.enrollmentSite.updateMirrorFields.useMutation({
    onError: createMutationErrorHandler('Could not update mirror settings'),
  });

  const rows = useMemo(() => {
    return sections.map((section) => ({
      section,
      fields: ALWAYS_OWN_SECTIONS.has(section.id)
        ? []
        : MIRROR_FIELDS.filter((f) => f.section === section.id),
    }));
  }, [sections]);

  function toggleField(key: string) {
    setFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleSection(keys: string[], on: boolean) {
    setFields((prev) => {
      const next = new Set(prev);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirm() {
    if (!source || fields.size === 0 || busy) return;
    setBusy(true);
    try {
      const updatedAt = dirty ? await saveFirst() : lastKnownUpdatedAt;
      const picked = [...fields] as MirrorFieldKey[];
      const res =
        mode === 'start'
          ? await start.mutateAsync({
              groupSlug,
              sourceSlug: source.groupSlug,
              fields: picked,
              lastKnownUpdatedAt: updatedAt,
            })
          : await update.mutateAsync({groupSlug, fields: picked, lastKnownUpdatedAt: updatedAt});
      onApplied(res as {siteConfig: GroupConfig; updatedAt: string | Date}, source);
    } catch {
      // Mutation onError already reported it.
    } finally {
      setBusy(false);
    }
  }

  const picking = mode === 'start' && !source;
  const primaryLabel =
    mode === 'settings' ? 'Update' : dirty ? 'Save and mirror' : 'Start mirroring';

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>
            {mode === 'settings' ? 'Mirror settings' : 'Mirror another site'}
          </DialogTitle>
          <DialogDescription>
            {picking ? (
              'Pick the site this one will follow.'
            ) : (
              <>
                Checked fields follow{' '}
                <span className='font-medium text-foreground'>{source?.name}</span> and lock here.
                Unchecked fields stay this site’s own.
                {mode === 'start' && (
                  <>
                    {' '}
                    <button type='button' className='underline' onClick={() => setSource(null)}>
                      Change source
                    </button>
                  </>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {picking ? (
          isLoading ? (
            <div className='flex justify-center py-10'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : candidates.length === 0 ? (
            <p className='py-8 text-center text-sm text-muted-foreground'>
              No sites can be a source yet.
            </p>
          ) : (
            <div className='max-h-80 space-y-1 overflow-y-auto'>
              {candidates.map((s) => (
                <button
                  key={s.id}
                  type='button'
                  onClick={() => pickSource({groupSlug: s.groupSlug, name: s.name})}
                  className='flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-muted/50'
                >
                  <span
                    className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold text-white'
                    style={{backgroundColor: s.primaryColor || '#1F3A5F'}}
                  >
                    {s.name.slice(0, 2).toUpperCase()}
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-sm font-medium'>{s.name}</span>
                    <span className='block text-xs text-muted-foreground'>
                      {s.planCount} plan{s.planCount === 1 ? '' : 's'}
                      {s.mirroredByCount > 0 &&
                        ` · source for ${s.mirroredByCount} site${s.mirroredByCount === 1 ? '' : 's'}`}
                    </span>
                  </span>
                  <ChevronRight className='h-4 w-4 shrink-0 text-muted-foreground' />
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            {dirty && (
              <div className='flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
                <p>
                  You have unsaved changes. They’ll be saved first, then the checked fields are
                  replaced with {source?.name}’s content.
                </p>
              </div>
            )}
            <div className='max-h-[26rem] divide-y overflow-y-auto rounded-lg border'>
              {rows.map(({section, fields: sectionFields}) => (
                <SectionRow
                  key={section.id}
                  section={section}
                  sectionFields={sectionFields}
                  checked={fields}
                  expanded={expanded.has(section.id)}
                  onToggleExpanded={() => toggleExpanded(section.id)}
                  onToggleSection={toggleSection}
                  onToggleField={toggleField}
                  fieldNote={fieldNote}
                />
              ))}
            </div>
            {ratesPeriodMismatch && sourcePeriod && ownPeriod && (
              <div className='flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
                <p>
                  {source?.name} is {sourcePeriod.adjective} and this site is {ownPeriod.adjective}.
                  Rates are entered per pay period, so the mirrored {sourcePeriod.adjective} rates
                  would show here {ownPeriod.each}.{' '}
                  <button
                    type='button'
                    className='font-semibold underline'
                    onClick={() => onSetPayPeriod(sourceSite?.siteConfig.payPeriod ?? 'weekly')}
                  >
                    Switch this site to {sourcePeriod.adjective}
                  </button>{' '}
                  or uncheck Rates follow the source.
                </p>
              </div>
            )}
            {sites && !keepsCoreProduct && (
              <div className='flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
                <p>
                  With these choices this site has no Medical or Limited medical, so it can’t publish
                  until one is added.
                </p>
              </div>
            )}
          </>
        )}

        {!picking && (
          <DialogFooter className='items-center sm:justify-between'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() =>
                setFields(source ? defaultFieldsFor(source.groupSlug) : new Set(DEFAULT_MIRROR_FIELDS))
              }
              disabled={busy}
            >
              Reset to default
            </Button>
            <div className='flex gap-2'>
              <Button variant='outline' size='sm' onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              {/* With rates following, wait for the source's pay period so a mismatch can't be missed. */}
              <Button
                size='sm'
                onClick={() => void confirm()}
                disabled={busy || fields.size === 0 || (fields.has('rates') && !sourceSite)}
              >
                {busy && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
                {primaryLabel}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionRow({
  section,
  sectionFields,
  checked,
  expanded,
  onToggleExpanded,
  onToggleSection,
  onToggleField,
  fieldNote,
}: {
  section: SectionOption;
  sectionFields: readonly {key: string; label: string}[];
  checked: ReadonlySet<string>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleSection: (keys: string[], on: boolean) => void;
  onToggleField: (key: string) => void;
  fieldNote: (key: string) => string | undefined;
}) {
  const Icon = section.icon;
  const keys = sectionFields.map((f) => f.key);
  const count = keys.filter((k) => checked.has(k)).length;
  const alwaysOwn = keys.length === 0;
  const all = !alwaysOwn && count === keys.length;
  const some = count > 0 && !all;
  const single = keys.length === 1;

  return (
    <div className={cn('px-3 py-2.5', alwaysOwn && 'bg-muted/30')}>
      <div className='flex items-center gap-3'>
        {alwaysOwn ? (
          <Lock className='h-4 w-4 shrink-0 text-muted-foreground' />
        ) : (
          <TriStateCheckbox
            checked={all}
            indeterminate={some}
            onChange={() => onToggleSection(keys, !all)}
            label={`Mirror all of ${section.label}`}
          />
        )}
        <Icon className='h-4 w-4 shrink-0 text-muted-foreground' />
        <span className='flex-1'>
          <span className='block text-sm font-medium'>{section.label}</span>
          {section.hint && (
            <span className='block text-xs text-muted-foreground'>{section.hint}</span>
          )}
        </span>
        <span className='text-xs text-muted-foreground'>
          {alwaysOwn
            ? 'Always this site’s'
            : all
              ? 'All mirrored'
              : count === 0
                ? 'This site’s own'
                : `${count} of ${keys.length} mirrored`}
        </span>
        {!alwaysOwn && !single ? (
          <button
            type='button'
            onClick={onToggleExpanded}
            className='rounded p-0.5 text-muted-foreground hover:bg-muted'
            aria-label={expanded ? `Collapse ${section.label}` : `Expand ${section.label}`}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
          </button>
        ) : (
          <span className='w-5' />
        )}
      </div>
      {expanded && !single && (
        <div className='mt-2 grid gap-x-4 gap-y-1.5 pl-7 sm:grid-cols-2'>
          {sectionFields.map((f) => (
            <label key={f.key} className='flex items-start gap-2 text-sm'>
              <input
                type='checkbox'
                className='mt-0.5'
                checked={checked.has(f.key)}
                onChange={() => onToggleField(f.key)}
              />
              <span className='min-w-0'>
                <span className='block'>{f.label}</span>
                {fieldNote(f.key) && (
                  <span className='block text-xs text-muted-foreground first-letter:uppercase'>
                    {fieldNote(f.key)}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function TriStateCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input ref={ref} type='checkbox' checked={checked} onChange={onChange} aria-label={label} />
  );
}
